import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, PartyPopper, Users, Link2, Sparkles, Lock, Globe, UserPlus, X, MessageCircle, Shield } from "lucide-react";

/* -----------------------------------------
   Small UI helpers (match dark glass vibe)
------------------------------------------ */
function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

const GlassShell = ({ title, subtitle, children, onBack }) => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070b1a]">
      {/* subtle depth (NO gradients) */}
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-[620px] w-[620px] rounded-full bg-indigo-400/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-6xl px-4 py-10 md:py-14">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          {/* Back button top-left */}
          <button
            type="button"
            onClick={onBack}
            className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm font-medium text-white/80 backdrop-blur-xl hover:bg-white/[0.06] transition"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="min-w-0 text-right">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[10px] uppercase tracking-[0.34em] text-white/65 backdrop-blur-xl">
              <Sparkles className="h-3.5 w-3.5 text-emerald-300/80" />
              Watch Parties • Creator hub
            </div>

            <h1 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-1 max-w-2xl text-sm text-white/55 ml-auto">
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>

        {/* Content card */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.03] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_24px_80px_rgba(0,0,0,0.55)] backdrop-blur-2xl">
          {/* subtle highlights (NO gradients) */}
          <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-[520px] -translate-x-1/2 rounded-full bg-emerald-400/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 right-[-60px] h-56 w-56 rounded-full bg-white/5 blur-3xl" />
          <div className="relative p-5 sm:p-8">{children}</div>
        </div>
      </div>
    </div>
  );
};

const FieldLabel = ({ icon: Icon, children }) => (
  <div className="mb-2 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-white/55">
    {Icon ? <Icon className="h-4 w-4 text-emerald-300/70" /> : null}
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={cx(
      "w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3",
      "text-white/90 placeholder:text-white/35",
      "backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
      "focus:outline-none focus:ring-2 focus:ring-emerald-300/30 focus:border-emerald-300/30",
      "disabled:opacity-50",
      props.className
    )}
  />
);

const Select = (props) => (
  <select
    {...props}
    className={cx(
      "w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3",
      "text-white/90",
      "backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
      "focus:outline-none focus:ring-2 focus:ring-emerald-300/30 focus:border-emerald-300/30",
      "disabled:opacity-50",
      "[&>option]:bg-slate-900 [&>option]:text-white",
      "[&>option:disabled]:text-white/40",
      props.className
    )}
  />
);

const Hint = ({ children, tone = "muted" }) => {
  const toneClass =
    tone === "good"
      ? "text-emerald-300/80"
      : tone === "bad"
      ? "text-rose-300/80"
      : "text-white/40";
  return <p className={cx("mt-2 text-xs", toneClass)}>{children}</p>;
};

function StartWatchPartyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isCreating, setIsCreating] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    maxCapacity: 10,
    platform: "YOUTUBE",
    videoUrl: "/videos/test-video.mp4",
    youtubeUrl: "",
    vimeoUrl: "",
    dailymotionUrl: "",
    externalUrl: "",
    seasonNumber: "",
    episodeNumber: "",
    episodeTitle: "",
    allowNsfwChat: false,
  });

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const cleanUrl = url.trim();

    let match = cleanUrl.match(
      /(?:youtube\.com\/watch\?.*v=)([a-zA-Z0-9_-]{11})/
    );
    if (match) return match[1];

    match = cleanUrl.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];

    match = cleanUrl.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];

    match = cleanUrl.match(/(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];

    match = cleanUrl.match(/(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
    if (match) return match[1];

    if (/^[a-zA-Z0-9_-]{11}$/.test(cleanUrl)) return cleanUrl;
    return null;
  };

  // (kept for future)
  const extractVimeoId = (url) => {
    if (!url) return null;
    const cleanUrl = url.trim();
    let match = cleanUrl.match(/(?:vimeo\.com\/)([0-9]{8,})/);
    if (match) return match[1];
    if (/^[0-9]{8,}$/.test(cleanUrl)) return cleanUrl;
    return null;
  };

  const extractDailymotionId = (url) => {
    if (!url) return null;
    const cleanUrl = url.trim();
    let match = cleanUrl.match(/(?:dailymotion\.com\/video\/)([a-zA-Z0-9]+)/);
    if (match) return match[1];
    match = cleanUrl.match(/(?:dai\.ly\/)([a-zA-Z0-9]+)/);
    if (match) return match[1];
    if (/^[a-zA-Z0-9]+$/.test(cleanUrl)) return cleanUrl;
    return null;
  };

  const ytId = useMemo(
    () => extractYouTubeId(formData.youtubeUrl),
    [formData.youtubeUrl]
  );

  useEffect(() => {
    if (!user) {
      alert("Please log in to create a watch party");
      navigate("/login");
    }
  }, [user, navigate]);

  const fetchFriends = async () => {
    if (!user) return;
    try {
      setLoadingFriends(true);
      const res = await axios.get(`/api/friends/${user.id}`, {
        withCredentials: true,
      });
      setFriends(res.data || []);
    } catch (error) {
      console.error("Error fetching friends:", error);
    } finally {
      setLoadingFriends(false);
    }
  };

  const handlePrivacyChange = (isPublicValue) => {
    setIsPublic(isPublicValue);
    if (!isPublicValue) {
      // Open friends modal when switching to private
      setShowFriendsModal(true);
      if (friends.length === 0) {
        fetchFriends();
      }
    } else {
      // Clear selected friends when switching to public
      setSelectedFriends([]);
    }
  };

  const toggleFriendSelection = (friendId) => {
    setSelectedFriends((prev) =>
      prev.includes(friendId)
        ? prev.filter((id) => id !== friendId)
        : [...prev, friendId]
    );
  };

  const selectAllFriends = () => {
    setSelectedFriends(friends.map((f) => f.user_id));
  };

  const deselectAllFriends = () => {
    setSelectedFriends([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert("Please enter a party title");
      return;
    }

    let finalVideoUrl = formData.videoUrl;

    if (formData.platform === "YOUTUBE") {
      if (!ytId) {
        alert("Please enter a valid YouTube URL");
        return;
      }
      finalVideoUrl = ytId;
    } else if (formData.platform === "VIMEO") {
      const videoId = extractVimeoId(formData.vimeoUrl);
      if (!videoId) {
        alert("Please enter a valid Vimeo URL");
        return;
      }
      finalVideoUrl = videoId;
    } else if (formData.platform === "DAILYMOTION") {
      const videoId = extractDailymotionId(formData.dailymotionUrl);
      if (!videoId) {
        alert("Please enter a valid Dailymotion URL");
        return;
      }
      finalVideoUrl = videoId;
    }

    const isExternalPlatform = [
      "CRUNCHYROLL",
      "NETFLIX",
      "HULU",
      "DISNEY_PLUS",
      "FUNIMATION",
    ].includes(formData.platform);

    if (isExternalPlatform) {
      if (!formData.externalUrl || !formData.externalUrl.trim()) {
        alert("Please enter the episode/movie URL");
        return;
      }
      finalVideoUrl = formData.externalUrl.trim();
    }

    setIsCreating(true);

    try {
      const payload = {
        title: formData.title.trim(),
        max_capacity: formData.maxCapacity,
        platform: formData.platform,
        video_url: finalVideoUrl,
        is_public: isPublic,
        invited_friends: !isPublic ? selectedFriends : [],
        allow_nsfw_chat: formData.allowNsfwChat,
      };

      if (isExternalPlatform) {
        if (formData.seasonNumber)
          payload.season_number = parseInt(formData.seasonNumber, 10);
        if (formData.episodeNumber)
          payload.episode_number = parseInt(formData.episodeNumber, 10);
        if (formData.episodeTitle)
          payload.episode_title = formData.episodeTitle.trim();
      }

      const response = await axios.post("/api/watch-parties", payload, {
        withCredentials: true,
      });

      navigate(`/watch-party/${response.data.id}`);
    } catch (error) {
      console.error("Error creating party:", error);
      if (error.response?.status === 401) {
        alert("Please log in to create a watch party");
        navigate("/login");
      } else {
        alert("Failed to create watch party. Please try again.");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (!user) return null;

  return (
    <GlassShell
      title="Start a watch party"
      subtitle="Set up a room, drop a link, and invite people to watch together."
      onBack={() => navigate("/watch-parties")}
    >
      <form onSubmit={handleSubmit} className="space-y-7">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8 space-y-6">
            {/* Privacy Selection */}
            <div>
              <FieldLabel icon={isPublic ? Globe : Lock}>Privacy</FieldLabel>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handlePrivacyChange(true)}
                  disabled={isCreating}
                  className={cx(
                    "rounded-2xl border px-4 py-3 text-sm font-medium transition",
                    "backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
                    "disabled:opacity-50",
                    isPublic
                      ? "border-emerald-300/40 bg-emerald-300/20 text-emerald-100"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.06]"
                  )}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Globe className="h-4 w-4" />
                    <span>Public</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handlePrivacyChange(false)}
                  disabled={isCreating}
                  className={cx(
                    "rounded-2xl border px-4 py-3 text-sm font-medium transition",
                    "backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
                    "disabled:opacity-50",
                    !isPublic
                      ? "border-purple-300/40 bg-purple-300/20 text-purple-100"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.06]"
                  )}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Lock className="h-4 w-4" />
                    <span>Private</span>
                  </div>
                </button>
              </div>
              <Hint>
                {isPublic
                  ? "Anyone can discover and join your watch party."
                  : "Only invited friends can see and join your watch party."}
              </Hint>
            </div>

            {/* Invite Friends button for private parties */}
            {!isPublic && (
              <div>
                <FieldLabel icon={UserPlus}>Invite Friends</FieldLabel>
                <button
                  type="button"
                  onClick={() => {
                    setShowFriendsModal(true);
                    if (friends.length === 0) {
                      fetchFriends();
                    }
                  }}
                  disabled={isCreating}
                  className={cx(
                    "w-full rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3",
                    "text-white/90 backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
                    "hover:bg-white/[0.06] transition disabled:opacity-50",
                    "flex items-center justify-between"
                  )}
                >
                  <span className="text-sm">
                    {selectedFriends.length === 0
                      ? "Select friends to invite"
                      : `${selectedFriends.length} friend${selectedFriends.length === 1 ? "" : "s"} selected`}
                  </span>
                  <UserPlus className="h-4 w-4" />
                </button>
                <Hint>
                  {selectedFriends.length === 0
                    ? "Select friends from your list to invite them."
                    : "Selected friends will be able to join this watch party."}
                </Hint>
              </div>
            )}

            {/* Chat Filter Selection */}
            <div>
              <FieldLabel icon={MessageCircle}>Chat Mode</FieldLabel>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, allowNsfwChat: false }))}
                  disabled={isCreating}
                  className={cx(
                    "rounded-2xl border px-4 py-3 text-sm font-medium transition",
                    "backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
                    "disabled:opacity-50",
                    !formData.allowNsfwChat
                      ? "border-emerald-300/40 bg-emerald-300/20 text-emerald-100"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.06]"
                  )}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Shield className="h-4 w-4" />
                    <span>Filtered</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, allowNsfwChat: true }))}
                  disabled={isCreating}
                  className={cx(
                    "rounded-2xl border px-4 py-3 text-sm font-medium transition",
                    "backdrop-blur-xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
                    "disabled:opacity-50",
                    formData.allowNsfwChat
                      ? "border-orange-300/40 bg-orange-300/20 text-orange-100"
                      : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.06]"
                  )}
                >
                  <div className="flex items-center justify-center gap-2">
                    <MessageCircle className="h-4 w-4" />
                    <span>Unfiltered (18+)</span>
                  </div>
                </button>
              </div>
              <Hint>
                {!formData.allowNsfwChat
                  ? "Chat will have profanity filtering enabled."
                  : "⚠️ Unfiltered chat. Participants will see a warning and must accept before joining."}
              </Hint>
            </div>

            <div>
              <FieldLabel icon={PartyPopper}>Party title</FieldLabel>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Friday Night Anime Marathon"
                required
                disabled={isCreating}
                maxLength={100}
              />
              <Hint>Keep it short—this is what shows in the lobby list.</Hint>
            </div>

            <div>
              <FieldLabel icon={Link2}>Platform</FieldLabel>
              <Select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                disabled={isCreating}
              >
                <option value="YOUTUBE">YouTube</option>
                <option value="CRUNCHYROLL">Crunchyroll (Metadata Sync)</option>
                <option value="NETFLIX">Netflix (Metadata Sync)</option>
                <option value="HULU">Hulu (Metadata Sync)</option>
                <option value="DISNEY_PLUS">Disney+ (Metadata Sync)</option>
                <option value="FUNIMATION">Funimation (Metadata Sync)</option>
              </Select>
              <Hint>
                {formData.platform === "YOUTUBE"
                  ? "YouTube videos are embedded with automatic sync."
                  : ["CRUNCHYROLL", "NETFLIX", "HULU", "DISNEY_PLUS", "FUNIMATION"].includes(formData.platform)
                  ? "⚠️ Metadata sync: Each participant must have their own subscription and will manually sync."
                  : "Choose your preferred platform for watch parties."}
              </Hint>
            </div>

            {formData.platform === "YOUTUBE" && (
              <div>
                <FieldLabel icon={Link2}>YouTube video URL</FieldLabel>
                <Input
                  type="text"
                  name="youtubeUrl"
                  value={formData.youtubeUrl}
                  onChange={handleChange}
                  placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                  required
                  disabled={isCreating}
                />

                {formData.youtubeUrl ? (
                  ytId ? (
                    <Hint tone="good">Valid YouTube video detected: {ytId}</Hint>
                  ) : (
                    <Hint tone="bad">Invalid YouTube URL format</Hint>
                  )
                ) : (
                  <Hint>Paste any YouTube link (watch, share, shorts, embed).</Hint>
                )}
              </div>
            )}

            {/* External Platforms - Metadata Sync */}
            {["CRUNCHYROLL", "NETFLIX", "HULU", "DISNEY_PLUS", "FUNIMATION"].includes(formData.platform) && (
              <div className="space-y-6">
                <div>
                  <FieldLabel icon={Link2}>Episode/Movie URL</FieldLabel>
                  <Input
                    type="text"
                    name="externalUrl"
                    value={formData.externalUrl}
                    onChange={handleChange}
                    placeholder={
                      formData.platform === "CRUNCHYROLL"
                        ? "https://www.crunchyroll.com/watch/..."
                        : formData.platform === "NETFLIX"
                        ? "https://www.netflix.com/watch/..."
                        : formData.platform === "HULU"
                        ? "https://www.hulu.com/watch/..."
                        : formData.platform === "DISNEY_PLUS"
                        ? "https://www.disneyplus.com/video/..."
                        : "https://www.funimation.com/..."
                    }
                    required
                    disabled={isCreating}
                  />
                  <Hint>
                    Copy the URL from your browser when viewing the episode/movie.
                    All participants will need to open this URL in their own browser.
                  </Hint>
                </div>

                {/* Optional Episode Info */}
                <div className="rounded-2xl border border-amber-400/20 bg-amber-400/5 p-4">
                  <div className="mb-3 text-sm font-semibold text-amber-200">
                    🎬 Episode Info (Optional)
                  </div>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs text-white/60">Season #</label>
                        <Input
                          type="number"
                          name="seasonNumber"
                          value={formData.seasonNumber}
                          onChange={handleChange}
                          placeholder="1"
                          min="1"
                          disabled={isCreating}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-white/60">Episode #</label>
                        <Input
                          type="number"
                          name="episodeNumber"
                          value={formData.episodeNumber}
                          onChange={handleChange}
                          placeholder="1"
                          min="1"
                          disabled={isCreating}
                          className="text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-white/60">Episode Title</label>
                      <Input
                        type="text"
                        name="episodeTitle"
                        value={formData.episodeTitle}
                        onChange={handleChange}
                        placeholder="e.g., The Beginning of the End"
                        maxLength="100"
                        disabled={isCreating}
                        className="text-sm"
                      />
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-amber-200/70">
                    This info will be displayed in the watch party room to help everyone stay on the same page.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-4">
            <div className="rounded-[22px] border border-white/10 bg-white/[0.03] p-4 backdrop-blur-2xl shadow-[0_0_0_1px_rgba(255,255,255,0.02)]">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.34em] text-white/55">
                  Room settings
                </div>
                <div className="h-8 w-8 rounded-xl border border-white/10 bg-white/[0.03] grid place-items-center">
                  <Users className="h-4 w-4 text-emerald-300/70" />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-medium text-white/85">
                      Max participants
                    </div>
                    <div className="text-sm text-white/60">
                      {formData.maxCapacity}
                    </div>
                  </div>

                  <input
                    type="range"
                    name="maxCapacity"
                    value={formData.maxCapacity}
                    onChange={handleChange}
                    min="2"
                    max="50"
                    disabled={isCreating}
                    className={cx(
                      "w-full h-2 rounded-full appearance-none cursor-pointer",
                      "bg-white/10 disabled:opacity-50",
                      "[&::-webkit-slider-thumb]:appearance-none",
                      "[&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4",
                      "[&::-webkit-slider-thumb]:rounded-full",
                      "[&::-webkit-slider-thumb]:bg-emerald-300",
                      "[&::-webkit-slider-thumb]:shadow-[0_0_0_6px_rgba(16,185,129,0.12)]"
                    )}
                  />
                  <div className="mt-2 flex justify-between text-xs text-white/35">
                    <span>2</span>
                    <span>50</span>
                  </div>
                </div>

                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-4">
                  <div className="text-sm font-semibold text-emerald-100">
                    Host powers
                  </div>
                  <ul className="mt-2 space-y-1 text-xs text-emerald-100/80">
                    <li>• You control play / pause / seek</li>
                    <li>• Everyone stays synced in-room</li>
                    <li>• Live chat alongside the player</li>
                  </ul>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                  <div className="text-xs uppercase tracking-[0.34em] text-white/45">
                    Preview
                  </div>
                  <div className="mt-2 text-sm font-semibold text-white/85 line-clamp-2">
                    {formData.title.trim() || "Untitled watch party"}
                  </div>
                  <div className="mt-1 text-xs text-white/45">
                    Platform:{" "}
                    {formData.platform === "YOUTUBE"
                      ? "YouTube"
                      : formData.platform === "CRUNCHYROLL"
                      ? "Crunchyroll"
                      : formData.platform === "NETFLIX"
                      ? "Netflix"
                      : formData.platform === "HULU"
                      ? "Hulu"
                      : formData.platform === "DISNEY_PLUS"
                      ? "Disney+"
                      : formData.platform === "FUNIMATION"
                      ? "Funimation"
                      : formData.platform}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate("/watch-parties")}
            disabled={isCreating}
            className="inline-flex items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-3 text-sm font-medium text-white/75 backdrop-blur-xl hover:bg-white/[0.06] transition disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={
              isCreating ||
              !formData.title.trim() ||
              (formData.platform === "YOUTUBE" && !ytId)
            }
            className={cx(
              "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold",
              "bg-emerald-400/90 text-black hover:bg-emerald-300 transition",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
          >
            {isCreating ? (
              "Creating..."
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Create party
              </>
            )}
          </button>
        </div>
      </form>

      {/* Friends Selection Modal */}
      {showFriendsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setShowFriendsModal(false)}
        >
          <div
            className="relative w-full max-w-lg max-h-[80vh] overflow-hidden rounded-[28px] border border-white/10 bg-[#070b1a]/95 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_24px_80px_rgba(0,0,0,0.75)] backdrop-blur-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 border-b border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">
                    Select Friends to Invite
                  </h3>
                  <p className="mt-1 text-xs text-white/55">
                    {selectedFriends.length} of {friends.length} selected
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowFriendsModal(false)}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-2 text-white/70 hover:bg-white/[0.06] transition"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Select All / Deselect All */}
              {friends.length > 0 && (
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={selectAllFriends}
                    className="rounded-lg border border-emerald-300/30 bg-emerald-300/10 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-300/15 transition"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={deselectAllFriends}
                    className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs font-medium text-white/70 hover:bg-white/[0.06] transition"
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>

            {/* Friends List */}
            <div className="overflow-y-auto p-5" style={{ maxHeight: "calc(80vh - 180px)" }}>
              {loadingFriends ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-emerald-300" />
                    <p className="text-sm text-white/55">Loading friends...</p>
                  </div>
                </div>
              ) : friends.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Users className="mx-auto mb-3 h-12 w-12 text-white/20" />
                    <p className="text-sm text-white/55">No friends found</p>
                    <p className="mt-1 text-xs text-white/40">
                      Add friends to invite them to private watch parties
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {friends.map((friend) => (
                    <button
                      key={friend.user_id}
                      type="button"
                      onClick={() => toggleFriendSelection(friend.user_id)}
                      className={cx(
                        "w-full rounded-2xl border p-3 transition",
                        "flex items-center gap-3 text-left",
                        selectedFriends.includes(friend.user_id)
                          ? "border-emerald-300/40 bg-emerald-300/10"
                          : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                      )}
                    >
                      {/* Avatar */}
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-white/20">
                        {friend.profile_picture ? (
                          <img
                            src={friend.profile_picture}
                            alt={friend.username}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-emerald-400/20 to-purple-400/20 text-sm font-bold text-white/90">
                            {friend.username?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>

                      {/* Friend Info */}
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-white/90 truncate">
                          {friend.username}
                        </div>
                        {friend.bio && (
                          <div className="text-xs text-white/50 truncate">
                            {friend.bio}
                          </div>
                        )}
                      </div>

                      {/* Checkbox */}
                      <div
                        className={cx(
                          "h-5 w-5 shrink-0 rounded border-2 transition flex items-center justify-center",
                          selectedFriends.includes(friend.user_id)
                            ? "border-emerald-300 bg-emerald-300"
                            : "border-white/30"
                        )}
                      >
                        {selectedFriends.includes(friend.user_id) && (
                          <svg
                            className="h-3 w-3 text-black"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 border-t border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl">
              <button
                type="button"
                onClick={() => setShowFriendsModal(false)}
                className="w-full rounded-2xl bg-emerald-400/90 px-5 py-3 text-sm font-semibold text-black hover:bg-emerald-300 transition"
              >
                Done ({selectedFriends.length} selected)
              </button>
            </div>
          </div>
        </div>
      )}
    </GlassShell>
  );
}

export default StartWatchPartyPage;
